import React, { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from "react";
import EncabezadoCartaMinimalista from "./EncabezadoRestobar";
import "../styles/temasEstacionalesCarta.css";
import BuscarProducto from "./BuscarProducto";
import BarraCategorias from "./BarraCategorias";
import ProductoCard from "./ProductoCard";
import ModalProducto from "./ModalProducto";
import CarritoPanel from "./CarritoVista";
import { calcularSubtotalPreview } from "../utils/carritoCalculos";

const CapaDecorativaFiestasPatrias = lazy(() =>
  import("./CapaDecorativaFiestasPatrias")
);
const CapaDecorativaHalloween = lazy(() => import("./CapaDecorativaHalloween"));
const CapaDecorativaNavidad = lazy(() => import("./CapaDecorativaNavidad"));

const CAPA_POR_TEMA = {
  "fiestas-patrias": CapaDecorativaFiestasPatrias,
  halloween: CapaDecorativaHalloween,
  navidad: CapaDecorativaNavidad,
};

function CapaTemaActiva({ clave }) {
  const Capa = CAPA_POR_TEMA[clave];
  if (!Capa) {
    return null;
  }
  return (
    <Suspense fallback={null}>
      <Capa />
    </Suspense>
  );
}

const CONSULTA_MOVIL = "(max-width: 991.98px)";

function leerViewportMovil() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(CONSULTA_MOVIL).matches;
}

function unidadesCarrito(productos) {
  return (productos || []).reduce((total, item) => {
    const cantidad = Number(item?.cantidad);
    return total + (Number.isFinite(cantidad) && cantidad > 0 ? cantidad : 1);
  }, 0);
}

function coordenadaY(event) {
  const y = event.clientY ?? event.nativeEvent?.clientY;
  return Number.isFinite(y) ? y : null;
}

function totalCarrito(productos, modoEntrega, costoDelivery) {
  const subtotal = calcularSubtotalPreview(productos);
  if (modoEntrega !== "domicilio") {
    return subtotal;
  }
  return subtotal + Number(costoDelivery || 0);
}

function HojaCarritoMovil({ productos, modoEntrega, costoDelivery, children }) {
  const [movil, setMovil] = useState(leerViewportMovil);
  const [abierto, setAbierto] = useState(false);
  const ignorarClick = useRef(false);
  const arrastre = useRef(null);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }
    const media = window.matchMedia(CONSULTA_MOVIL);
    const sincronizar = () => setMovil(media.matches);
    sincronizar();
    media.addEventListener("change", sincronizar);
    return () => media.removeEventListener("change", sincronizar);
  }, []);

  useEffect(() => {
    if (!movil || !abierto) {
      return undefined;
    }
    const overflowBody = document.body.style.overflow;
    const overflowHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflowBody;
      document.documentElement.style.overflow = overflowHtml;
    };
  }, [movil, abierto]);

  if (!movil) {
    return <div className="carta-carrito">{children}</div>;
  }

  const unidades = unidadesCarrito(productos);
  const total = totalCarrito(productos, modoEntrega, costoDelivery);
  const etiqueta = abierto
    ? "Cerrar pedido"
    : unidades > 0
      ? `Abrir pedido, ${unidades} ${unidades === 1 ? "producto" : "productos"}`
      : "Abrir pedido";

  function alPulsar(event) {
    if (ignorarClick.current) {
      ignorarClick.current = false;
      event.preventDefault();
      return;
    }
    setAbierto((valor) => !valor);
  }

  function alPresionar(event) {
    const y = coordenadaY(event);
    if (y == null || (event.pointerType === "mouse" && event.button !== 0)) {
      return;
    }
    arrastre.current = {
      id: event.pointerId,
      y,
      t: performance.now(),
      dy: 0,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function alMover(event) {
    const gesto = arrastre.current;
    const y = coordenadaY(event);
    if (!gesto || gesto.id !== event.pointerId || y == null) {
      return;
    }
    gesto.dy = y - gesto.y;
  }

  function alSoltar(event) {
    const gesto = arrastre.current;
    if (!gesto || gesto.id !== event.pointerId) {
      return;
    }
    arrastre.current = null;
    const dy = Number.isFinite(gesto.dy) ? gesto.dy : 0;
    const dt = performance.now() - gesto.t;
    if (Math.abs(dy) < 10) {
      return;
    }
    ignorarClick.current = true;
    if (dy < -24 || (dy < -12 && dt < 280)) {
      setAbierto(true);
    } else if (dy > 24 || (dy > 12 && dt < 280)) {
      setAbierto(false);
    }
  }

  return (
    <>
      {abierto ? (
        <button
          type="button"
          className="carta-carrito-backdrop"
          aria-label="Cerrar pedido"
          onClick={() => setAbierto(false)}
        />
      ) : null}
      <div className={`carta-carrito${abierto ? " is-open" : ""}`}>
        <button
          type="button"
          className="carta-carrito-barra"
          aria-expanded={abierto}
          aria-controls="carta-carrito-panel"
          aria-label={etiqueta}
          onClick={alPulsar}
          onPointerDown={alPresionar}
          onPointerMove={alMover}
          onPointerUp={alSoltar}
          onPointerCancel={() => {
            arrastre.current = null;
          }}
        >
          <span className="carta-carrito-agarre" aria-hidden="true" />
          <span className="carta-carrito-fila">
            <span className="carta-carrito-resumen">
              <i className="bi bi-cart3" aria-hidden="true" />
              <span className="carta-carrito-titulo">Mi pedido</span>
              {unidades > 0 ? (
                <span className="carta-carrito-cuenta">
                  {unidades} {unidades === 1 ? "producto" : "productos"}
                </span>
              ) : null}
              <span className="carta-carrito-meta">
                {unidades > 0 ? (
                  <span className="carta-carrito-monto">
                    ${total.toLocaleString("es-CL")}
                  </span>
                ) : null}
              </span>
            </span>
            <i className="bi bi-chevron-up carta-carrito-flecha" aria-hidden="true" />
          </span>
        </button>
        <div
          id="carta-carrito-panel"
          className="carta-carrito-cuerpo"
          role="region"
          aria-label="Mi pedido"
          inert={!abierto}
          aria-hidden={abierto ? undefined : true}
        >
          <div className="carta-carrito-cuerpo-inner">{children}</div>
        </div>
      </div>
    </>
  );
}

export default function CartaDigitalVista({
  sucursalSeleccionada,
  temaEstacional = null,
  inicializandoSucursal,
  onLocalizarTienda,
  modoEntrega,
  setModoEntrega,
  busqueda,
  setBusqueda,
  categoriaActiva,
  setCategoriaActiva,
  categoriasDisponibles,
  cargandoProductos,
  productosFiltrados,
  abrirModal,
  carrito,
  eliminar,
  editar,
  cambiarCantidad,
  vaciar,
  costoDelivery,
  horariosDisponibles,
  horarioSeleccionado,
  setHorarioSeleccionado,
  pedidosCerrados = false,
  mensajeCierre = null,
  finalizarPedido,
  enviandoPedido,
  productoSeleccionado,
  modalAbierto,
  cerrarModal,
  agregar,
  localNombre,
  extrasDisponibles = [],
  comentarioInicial = "",
  modoEdicionOpciones = false,
}) {
  const claveTema = temaEstacional?.clave || undefined;
  const anclaCartaRef = useRef(null);
  const omitirScrollInicial = useRef(true);
  const indiceImagenPrioritaria = (productosFiltrados || []).findIndex(
    (producto) => producto?.imagen_url
  );

  useLayoutEffect(() => {
    if (omitirScrollInicial.current) {
      omitirScrollInicial.current = false;
      return;
    }
    anclaCartaRef.current?.scrollIntoView({ block: "start" });
  }, [categoriaActiva]);

  return (
    <div data-carta-tema={claveTema}>
      <CapaTemaActiva clave={claveTema} />
      <EncabezadoCartaMinimalista
        onLocalizarTienda={onLocalizarTienda}
        modoEntrega={modoEntrega}
        setModoEntrega={setModoEntrega}
        sucursalSeleccionada={sucursalSeleccionada}
        inicializandoSucursal={inicializandoSucursal}
        localNombre={localNombre}
      />

      <div className="container my-4">
        {!sucursalSeleccionada ? (
          <div className="text-center p-5 text-secondary">
            {inicializandoSucursal
              ? "Cargando sucursal..."
              : "No hay sucursal disponible para esta carta."}
          </div>
        ) : (
          <>
            <h2 className="fw-bold mb-3">
              Carta{" "}
              <span className="text-secondary fw-normal">
                - {sucursalSeleccionada.nombre}
              </span>
            </h2>

            {pedidosCerrados && mensajeCierre && (
              <div className="alert alert-secondary rounded-4" role="status">
                {mensajeCierre}
              </div>
            )}

            <div className="row">
              <div className="col-lg-8 carta-productos">
                <div
                  ref={anclaCartaRef}
                  className="card shadow-sm p-4 mb-4 carta-ancla"
                  style={{ borderRadius: 24 }}
                >
                  <BuscarProducto valor={busqueda} setValor={setBusqueda} />
                  <BarraCategorias
                    categorias={categoriasDisponibles}
                    activa={categoriaActiva}
                    setActiva={setCategoriaActiva}
                  />

                  <div className="row mt-3">
                    {cargandoProductos ? (
                      <div className="p-5 text-center text-muted">
                        Cargando productos...
                      </div>
                    ) : productosFiltrados.length === 0 ? (
                      <div className="p-5 text-center text-muted">
                        No hay productos disponibles
                      </div>
                    ) : (
                      productosFiltrados.map((producto, index) => (
                        <div
                          key={producto.id}
                          className="col-md-6 col-xl-4 mb-4"
                        >
                          <ProductoCard
                            producto={producto}
                            onClick={abrirModal}
                            prioridad={index === indiceImagenPrioritaria}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <HojaCarritoMovil
                  productos={carrito}
                  modoEntrega={modoEntrega}
                  costoDelivery={costoDelivery}
                >
                  <CarritoPanel
                    productos={carrito}
                    onEliminar={eliminar}
                    onEditar={editar}
                    onCambiarCantidad={cambiarCantidad}
                    onVaciar={vaciar}
                    modoEntrega={modoEntrega}
                    costoDelivery={costoDelivery}
                    horariosDisponibles={horariosDisponibles}
                    horarioSeleccionado={horarioSeleccionado}
                    onHorarioChange={setHorarioSeleccionado}
                    onFinalizar={finalizarPedido}
                    enviandoPedido={enviandoPedido}
                    pedidosCerrados={pedidosCerrados}
                    mensajeCierre={mensajeCierre}
                  />
                </HojaCarritoMovil>
              </div>
            </div>
          </>
        )}
      </div>

      <ModalProducto
        producto={productoSeleccionado}
        abierto={modalAbierto}
        onCerrar={cerrarModal}
        onAgregar={agregar}
        sucursalId={sucursalSeleccionada?.id}
        extrasDisponibles={extrasDisponibles}
        comentarioInicial={comentarioInicial}
        modoEdicionOpciones={modoEdicionOpciones}
      />
    </div>
  );
}
